import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { IconName } from "@fortawesome/fontawesome-svg-core";

import type { SlashCommandType } from "../DocumentView/DocumentContext";
import { cn } from "../lib/utils";

export interface SlashCommandMenuProps {
  items: SlashCommandType[];
  activeIndex: number;
  /** Screen coordinates to anchor the menu at (top-left, fixed positioning). */
  position: { top: number; left: number };
  onSelect: (item: SlashCommandType) => void;
  onHover: (index: number) => void;
}

/**
 * The floating `/` command menu — a filtered list of block types the current
 * block can convert into. Purely presentational + click/hover; arrow-key
 * navigation and selection are driven by the host editor (which owns
 * `activeIndex` so keystrokes stay in the editor).
 */
const SlashCommandMenu: React.FC<SlashCommandMenuProps> = ({
  items,
  activeIndex,
  position,
  onSelect,
  onHover,
}) => {
  const activeRef = useRef<HTMLButtonElement | null>(null);

  // Keep the highlighted row visible as the user arrows through a long list.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (items.length === 0) return null;

  return createPortal(
    <div
      style={{ top: position.top, left: position.left }}
      className="fixed z-[9999] max-h-[280px] w-[240px] overflow-y-auto rounded-lg border border-[#e6e9ef] bg-white py-1 shadow-[0_8px_28px_rgba(15,23,42,0.16)]"
      // Keep the editor selection/focus while clicking the menu.
      onMouseDown={(e) => e.preventDefault()}
    >
      {items.map((item, i) => (
        <button
          key={item.typeId}
          ref={i === activeIndex ? activeRef : undefined}
          type="button"
          onMouseEnter={() => onHover(i)}
          onClick={() => onSelect(item)}
          className={cn(
            "flex w-full items-center gap-2.5 px-2 py-1.5 text-left text-[13px] transition-colors",
            i === activeIndex ? "bg-[#2B7AEA]/10 text-[#1f2a3d]" : "text-[#3b4657]",
          )}
        >
          <span className="flex h-[26px] w-[26px] flex-shrink-0 items-center justify-center rounded-[6px] border border-[#eef1f4] bg-[#f7f9fc] text-[#5b6472]">
            <FontAwesomeIcon
              icon={["far", (item.icon || "circle") as IconName]}
              className="h-3.5 w-3.5"
            />
          </span>
          <span className="truncate font-medium">{item.name}</span>
        </button>
      ))}
    </div>,
    document.body,
  );
};

export default SlashCommandMenu;
